import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tooltip, Tag } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import api from '../../services/api';

const PersistentLocationTracker = ({ isAuthenticated }) => {
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const getCurrentPosition = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        (error) => reject(error),
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
      );
    });
  }, []);

  const updateLocation = useCallback(async () => {
    try {
      const { latitude, longitude } = await getCurrentPosition();
      await api.post('/delivery/location/update', {
        latitude,
        longitude,
        address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
      });
      setIsSharing(true);
      setError(null);
    } catch (err) {
      if (err.code === 1) {
        setError('Location permission denied');
        setIsSharing(false);
      }
    }
  }, [getCurrentPosition]);

  useEffect(() => {
    if (!isAuthenticated) return;

    updateLocation();

    intervalRef.current = setInterval(updateLocation, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAuthenticated, updateLocation]);

  return (
    <Tooltip title={isSharing ? 'Location sharing active' : error || 'Location sharing inactive'}>
      <Tag
        color={isSharing ? 'green' : error ? 'red' : 'orange'}
        style={{ cursor: 'pointer', marginRight: 8 }}
      >
        <EnvironmentOutlined style={{ marginRight: 4 }} />
        {isSharing ? 'Live' : error ? 'No GPS' : '...'}
      </Tag>
    </Tooltip>
  );
};

export default PersistentLocationTracker;
